import React, { useState } from 'react';
import { CheckSquare, X, Loader2, Trash2, ArrowRightLeft } from 'lucide-react';
import { Button } from './ui/button';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { Input } from './ui/input';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from './ui/alert-dialog';
import { useLanguage } from '../contexts/LanguageContext';
import { CARE_VOCAB } from './add-plant-steps/plantUtils';

const BULK_FIELDS = [
  { id: 'family', labelKey: 'family', type: 'text' },
  { id: 'plant_type', labelKey: 'plantType', type: 'text' },
  { id: 'watering', labelKey: 'watering', type: 'select' },
  { id: 'light', labelKey: 'light', type: 'select' },
  { id: 'humidity', labelKey: 'humidity', type: 'select' },
  { id: 'temperature', labelKey: 'temperature', type: 'select' },
  { id: 'toxicity', labelKey: 'toxicity', type: 'select' },
  { id: 'growth_rate', labelKey: 'growthRate', type: 'select' },
];

export const BulkEditBar = ({ selectedIds, totalCount, onSelectAll, onDeselectAll, onApply, onDelete, onMerge, onCancel, applying, deleting }) => {
  const { t } = useLanguage();
  const [field, setField] = useState('');
  const [value, setValue] = useState('');
  const [showConfirm, setShowConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  const selectedField = BULK_FIELDS.find(f => f.id === field);
  const options = selectedField ? (CARE_VOCAB[selectedField.id] || []) : [];
  const isSelect = selectedField?.type === 'select' && options.length > 0;
  const canApply = selectedIds.length > 0 && field && value.trim();

  const handleApply = () => {
    setShowConfirm(true);
  };

  const confirmApply = () => {
    setShowConfirm(false);
    onApply(field, value.trim());
  };

  const handleFieldChange = (newField) => {
    setField(newField);
    setValue('');
  };

  return (
    <>
      <div
        className="fixed bottom-0 left-0 right-0 z-50 border-t shadow-lg px-4 py-3"
        style={{ backgroundColor: 'hsl(143, 28%, 97%)', borderColor: 'hsl(143, 28%, 80%)' }}
        role="region"
        aria-label={t('bulkEditBarLabel') || 'Bulk edit'}
        data-testid="bulk-edit-bar"
      >
        <div className="container mx-auto flex flex-wrap items-center gap-3">
          {/* Selection controls */}
          <div className="flex items-center gap-2 mr-2">
            <CheckSquare className="w-5 h-5" style={{ color: 'hsl(143, 28%, 35%)' }} />
            <span className="text-sm font-semibold" style={{ color: 'hsl(143, 28%, 25%)' }} data-testid="bulk-selected-count">
              {selectedIds.length} {t('selected')}
            </span>
            <button
              onClick={selectedIds.length === totalCount ? onDeselectAll : onSelectAll}
              className="text-xs underline font-medium"
              style={{ color: 'hsl(143, 28%, 35%)' }}
              data-testid="bulk-select-all-toggle"
            >
              {selectedIds.length === totalCount ? t('deselectAll') : t('selectAll')}
            </button>
          </div>

          {/* Field picker */}
          <Select value={field} onValueChange={handleFieldChange}>
            <SelectTrigger
              className="w-40 h-9 text-sm"
              style={{ borderColor: 'hsl(143, 28%, 80%)', backgroundColor: 'white' }}
              aria-label={t('chooseField')}
              data-testid="bulk-field-select"
            >
              <SelectValue placeholder={t('chooseField')} />
            </SelectTrigger>
            <SelectContent>
              {BULK_FIELDS.map(f => (
                <SelectItem key={f.id} value={f.id} data-testid={`bulk-field-${f.id}`}>
                  {t(f.labelKey)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>

          {/* Value input */}
          {field && (
            isSelect ? (
              <Select value={value} onValueChange={setValue}>
                <SelectTrigger
                  className="w-44 h-9 text-sm"
                  style={{ borderColor: 'hsl(143, 28%, 80%)', backgroundColor: 'white' }}
                  aria-label={t('chooseValue')}
                  data-testid="bulk-value-select"
                >
                  <SelectValue placeholder={t('chooseValue')} />
                </SelectTrigger>
                <SelectContent>
                  {options.map(opt => (
                    <SelectItem key={opt} value={opt} data-testid={`bulk-value-${opt}`}>
                      {opt}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <Input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder={t('enterValue')}
                className="w-44 h-9 text-sm"
                style={{ borderColor: 'hsl(143, 28%, 80%)', backgroundColor: 'white' }}
                maxLength={100}
                data-testid="bulk-value-input"
              />
            )
          )}

          {/* Apply button */}
          <Button
            size="sm"
            disabled={!canApply || applying || deleting}
            onClick={handleApply}
            className="h-9 px-4"
            style={{
              backgroundColor: canApply && !applying && !deleting
                ? 'hsl(143, 28%, 25%)'
                : 'hsl(143, 28%, 90%)',
              color: canApply && !applying && !deleting
                ? 'white'
                : 'hsl(125, 12%, 50%)',
            }}
            data-testid="bulk-apply-button"
          >
            {applying ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{t('applying')}</> : t('applyToSelected')}
          </Button>

          {/* Merge button — only enabled when EXACTLY 2 plants are
              selected. Opens the merge diff dialog (target = first
              selected, source = second). Users can flip in the dialog. */}
          {onMerge && (
            <Button
              size="sm"
              disabled={selectedIds.length !== 2 || applying || deleting}
              onClick={() => onMerge(selectedIds)}
              className="h-9 px-4"
              style={{
                backgroundColor: selectedIds.length === 2 && !applying && !deleting
                  ? 'hsl(143, 28%, 25%)'
                  : 'hsl(143, 28%, 90%)',
                color: selectedIds.length === 2 && !applying && !deleting
                  ? 'white'
                  : 'hsl(125, 12%, 50%)',
              }}
              data-testid="bulk-merge-button"
              title={selectedIds.length === 2 ? (t('mergeSelected') || 'Merge selected') : (t('mergeRequiresTwo') || 'Select exactly 2 plants to merge')}
            >
              <ArrowRightLeft className="w-3.5 h-3.5 mr-1.5" />{t('mergeAction') || 'Merge'}
            </Button>
          )}

          {/* Delete Selected button */}
          <Button
            size="sm"
            disabled={selectedIds.length === 0 || applying || deleting}
            onClick={() => setShowDeleteConfirm(true)}
            className="h-9 px-4"
            style={{
              backgroundColor: selectedIds.length > 0 && !applying && !deleting
                ? 'hsl(0, 70%, 50%)'
                : 'hsl(143, 28%, 90%)',
              color: selectedIds.length > 0 && !applying && !deleting
                ? 'white'
                : 'hsl(125, 12%, 50%)',
            }}
            data-testid="bulk-delete-button"
          >
            {deleting ? <><Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />{t('deleting')}</> : <><Trash2 className="w-3.5 h-3.5 mr-1.5" />{t('deleteSelected')}</>}
          </Button>

          {/* Cancel */}
          <button
            onClick={onCancel}
            className="ml-auto p-2 rounded-full hover:bg-gray-200 transition-colors"
            aria-label={t('cancel')}
            title={t('exitBulkEdit')}
            data-testid="bulk-edit-cancel"
          >
            <X className="w-5 h-5" style={{ color: 'hsl(0, 50%, 45%)' }} />
          </button>
        </div>
      </div>

      {/* Confirmation dialog */}
      <AlertDialog open={showConfirm} onOpenChange={setShowConfirm}>
        <AlertDialogContent style={{ backgroundColor: 'white' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'hsl(143, 28%, 25%)', fontSize: '1.25rem' }} data-testid="bulk-confirm-title">
              {t('bulkEditConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'hsl(125, 12%, 25%)', fontSize: '0.95rem' }}>
              {t('bulkEditConfirmDesc')
                .replace('{count}', selectedIds.length)
                .replace('{field}', t(selectedField?.labelKey || ''))
                .replace('{value}', value)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={confirmApply} style={{ backgroundColor: 'hsl(143, 28%, 25%)', color: 'white' }} data-testid="bulk-confirm-apply">
              {t('applyChanges')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Delete confirmation dialog */}
      <AlertDialog open={showDeleteConfirm} onOpenChange={setShowDeleteConfirm}>
        <AlertDialogContent style={{ backgroundColor: 'white' }}>
          <AlertDialogHeader>
            <AlertDialogTitle style={{ color: 'hsl(0, 70%, 45%)', fontSize: '1.25rem' }} data-testid="bulk-delete-confirm-title">
              {t('bulkDeleteConfirmTitle')}
            </AlertDialogTitle>
            <AlertDialogDescription style={{ color: 'hsl(125, 12%, 25%)', fontSize: '0.95rem' }}>
              {t('bulkDeleteConfirmDesc').replace('{count}', selectedIds.length)}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel style={{ borderColor: 'hsl(143, 28%, 85%)', color: 'hsl(143, 28%, 25%)' }}>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => { setShowDeleteConfirm(false); onDelete(); }}
              style={{ backgroundColor: 'hsl(0, 70%, 50%)', color: 'white' }}
              data-testid="bulk-confirm-delete"
            >
              {t('delete')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
